import React, { useState, useEffect } from 'react';
import {
	Table,
	Input,
	Button,
	Row,
	Col,
	Card,
	CardHeader,
	CardBody,
	CardFooter,
	Alert,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	FormGroup,
	Label
} from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import resourceService from 'services/resource.service';

// Thêm style global cho Resource Table
const tableRowStyle = {
	cursor: 'pointer',
	transition: 'background-color 0.1s ease',
	userSelect: 'none', // Ngăn chặn việc bôi đen text khi click nhanh
};

const selectedRowStyle = {
	...tableRowStyle,
	backgroundColor: '#d3d3d3'
};

// Style cho bảng để tối ưu hiệu suất render
const tableStyle = {
	tableLayout: 'fixed', // Cố định layout của bảng
	borderCollapse: 'collapse',
	width: '100%',
};

// Style cho bảng
const cellStyle = {
	padding: '8px 12px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
};

// Style cho input trong table khi edit inline
const cellInputStyle = {
	width: '100%',
	padding: '6px',
	border: '1px solid #ccc',
	borderRadius: '4px',
	boxSizing: 'border-box'
};

// Style cho Alert notification
const notificationStyle = {
	position: 'fixed',
	top: '20px',
	left: '20px',
	zIndex: 1050,
	minWidth: '300px',
	boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
	animation: 'fadeIn 0.3s ease-in'
};

function Resources() {
	// State cho dữ liệu cookies và lựa chọn
	const [cookies, setCookies] = useState([]);
	const [selectedType, setSelectedType] = useState('DouyinCookie');
	const [selectedCookieId, setSelectedCookieId] = useState(null);

	// State cho edit inline
	const [editingRow, setEditingRow] = useState(null);
	const [editData, setEditData] = useState({ id: '', name: '', cookie: '', status: '', active: false });

	// State cho modal thêm mới
	const [addModal, setAddModal] = useState(false);
	const [newCookie, setNewCookie] = useState({
		cookie: '',
		status: 'live',
		active: false
	});

	// State cho notification Alert
	const [notification, setNotification] = useState({
		visible: false,
		color: 'success',
		message: ''
	});

	// State cho loading
	const [loading, setLoading] = useState(false);

	// Load dữ liệu khi component mount và khi selectedType thay đổi
	useEffect(() => {
		loadResources();
	}, [selectedType]);

	// Kiểm tra giá trị cookies trong state để debug
	useEffect(() => {
		console.log('Current cookies state:', cookies);
	}, [cookies]);

	// Tự động ẩn thông báo sau 3 giây
	useEffect(() => {
		let timer;
		if (notification.visible) {
			timer = setTimeout(() => {
				setNotification(prev => ({ ...prev, visible: false }));
			}, 3000);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [notification.visible]);

	// Hàm tạo ID ngẫu nhiên cho cookie
	const generateId = () => {
		return Math.random().toString(36).substring(2, 15);
	};

	// Hàm tạo tên cho cookie dựa vào status và active
	const generateName = (status, active) => {
		return `Cookie ${status} ${active ? '(Active)' : ''}`;
	};

	const loadResources = () => {
		setLoading(true);
		if (selectedType === 'DouyinCookie') {
			resourceService.getDouyinCookies()
				.then(res => {
					console.log('API response:', res); // Debug log
					if (res && res.success) {
						// Thêm id và name cho mỗi cookie
						const processedCookies = Array.isArray(res.data) ? res.data.map((cookie, index) => ({
							...cookie,
							id: generateId(),
							name: generateName(cookie.status, cookie.active)
						})) : [];

						setCookies(processedCookies);
						// Đã loại bỏ thông báo thành công khi load trang
					} else {
						showNotification('danger', 'Lỗi tải dữ liệu: ' + (res?.message || 'Không có dữ liệu'));
						console.error('API response error:', res);
					}
				})
				.catch(err => {
					showNotification('danger', `Lỗi: ${err.message}`);
					console.error('API request error:', err);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	};

	// Hiển thị thông báo
	const showNotification = (color, message) => {
		setNotification({
			visible: true,
			color,
			message
		});
	};

	// Đóng thông báo
	const onDismissNotification = () => {
		setNotification(prev => ({ ...prev, visible: false }));
	};

	// Xử lý chọn loại resource
	const handleTypeClick = (type) => {
		setSelectedType(type);
		setSelectedCookieId(null);
		setEditingRow(null);
	};

	// Xử lý chọn row
	const handleRowClick = (cookie, event) => {
		// Ngăn chặn event bubbling
		event.preventDefault();
		event.stopPropagation();

		// Bỏ qua sự kiện click nếu đang ở chế độ sửa
		if (editingRow !== null) {
			return;
		}

		// Sử dụng tracking biến để chắc chắn chỉ xử lý lần click đầu tiên
		if (event.detail > 1) {
			// Double-click: Bắt đầu sửa inline
			handleRowDoubleClick(cookie);
			return;
		}

		setSelectedCookieId(cookie.id);
	};

	// Xử lý double click để edit inline
	const handleRowDoubleClick = (cookie) => {
		// Set dòng đang sửa và dữ liệu edit
		setEditingRow(cookie.id);
		setEditData({
			id: cookie.id,
			name: cookie.name || '',
			cookie: cookie.cookie || '',
			status: cookie.status || '',
			active: cookie.active || false
		});

		// Đảm bảo có dòng được chọn khi sửa
		setSelectedCookieId(cookie.id);
	};

	// Cập nhật dữ liệu đang edit
	const handleEditChange = (field, value) => {
		// Xử lý đặc biệt cho trường active
		if (field === 'active' && value === 'true') {
			// Nếu người dùng đang set active = true cho cookie hiện tại
			// Cập nhật editData trước
			setEditData(prev => ({
				...prev,
				active: true
			}));
		} else {
			// Xử lý bình thường cho các trường khác
			setEditData(prev => ({
				...prev,
				[field]: field === 'active' ? value === 'true' : value
			}));
		}
	};

	// Hoàn thành edit và lưu thay đổi
	const handleEditComplete = () => {
		let updatedCookies = [...cookies];

		// Nếu đang set active = true cho cookie hiện tại, cập nhật tất cả cookie khác thành inactive
		if (editData.active) {
			updatedCookies = updatedCookies.map(item =>
				item.id === editingRow ? { ...item, ...editData } : { ...item, active: false }
			);
		} else {
			// Kiểm tra xem đang có bao nhiêu cookie active
			const otherActiveCookies = updatedCookies.filter(c => c.id !== editingRow && c.active).length;

			// Nếu không có cookie nào khác active và đang set cookie này là inactive
			if (otherActiveCookies === 0 && !editData.active) {
				showNotification('warning', 'Phải có ít nhất một cookie active');
				return;
			}

			// Cập nhật chỉ cookie đang edit
			updatedCookies = updatedCookies.map(item =>
				item.id === editingRow ? { ...item, ...editData } : item
			);
		}

		// Chuẩn bị dữ liệu gửi lên server (loại bỏ các trường id và name)
		const dataToSend = updatedCookies.map(({ id, name, ...rest }) => rest);

		console.log('Sending data to API:', dataToSend); // Debug log

		resourceService.updateDouyinCookies(dataToSend)
			.then(res => {
				console.log('Update response:', res); // Debug log
				if (res && res.success) {
					showNotification('success', res.message || 'Cập nhật cookie thành công');

					// Cập nhật state cookies với dữ liệu đã cập nhật
					setCookies(updatedCookies);

					// Chỉ load lại dữ liệu khi cần thiết
					// loadResources();
				} else {
					showNotification('danger', res?.message || 'Cập nhật thất bại');
					console.error('Update error:', res);
				}
			})
			.catch(err => {
				showNotification('danger', `Lỗi: ${err.message}`);
				console.error('Update request error:', err);
			})
			.finally(() => {
				setEditingRow(null);
			});
	};

	// Xử lý xóa cookie
	const handleDeleteCookie = (cookie) => {
		Swal.fire({
			title: 'Xác nhận xóa',
			text: 'Bạn có chắc chắn muốn xóa cookie này?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Xóa',
			cancelButtonText: 'Hủy',
			confirmButtonColor: '#dc3545'
		}).then((result) => {
			if (result.isConfirmed) {
				// Kiểm tra xem cookie đang xóa có đang active không
				const isActive = cookie.active;

				// Lọc bỏ cookie cần xóa
				const remainingCookies = cookies.filter(c => c.id !== cookie.id);

				// Nếu cookie bị xóa là active và còn cookies khác, thì set active cho cookie đầu tiên
				if (isActive && remainingCookies.length > 0) {
					remainingCookies[0].active = true;
				}

				// Chuẩn bị dữ liệu để gửi lên server
				const cookiesWithoutId = remainingCookies.map(({ id, name, ...rest }) => rest);

				resourceService.updateDouyinCookies(cookiesWithoutId)
					.then(res => {
						if (res && res.success) {
							showNotification('success', 'Xóa cookie thành công');
							setCookies(remainingCookies);
						} else {
							showNotification('danger', res?.message || 'Xóa cookie thất bại');
						}
					})
					.catch(err => {
						showNotification('danger', `Lỗi: ${err.message}`);
						console.error('Delete error:', err);
					});
			}
		});
	};

	// Xử lý thêm cookie mới
	const handleAddCookie = () => {
		// Kiểm tra dữ liệu hợp lệ
		if (!newCookie.cookie || newCookie.cookie.trim() === '') {
			showNotification('warning', 'Cookie không được để trống');
			return;
		}

		// Nếu người dùng đánh dấu cookie mới là active, đảm bảo các cookie khác không active
		const cookiesWithoutId = cookies.map(({ id, name, ...rest }) => rest);

		resourceService.addDouyinCookie(newCookie, cookiesWithoutId)
			.then(res => {
				if (res && res.success) {
					showNotification('success', 'Thêm cookie thành công');
					setAddModal(false);
					setNewCookie({
						cookie: '',
						status: 'live',
						active: false
					});
					loadResources();
				} else {
					showNotification('danger', res?.message || 'Thêm cookie thất bại');
				}
			})
			.catch(err => {
				showNotification('danger', `Lỗi: ${err.message}`);
				console.error('Add cookie error:', err);
			});
	};

	// Hủy edit
	const cancelEdit = () => {
		setEditingRow(null);
		setEditData({ id: '', name: '', cookie: '', status: '', active: false });
	};

	// Xử lý phím tắt khi đang edit
	const handleEditKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			// Enter để hoàn thành
			e.preventDefault();
			handleEditComplete();
		} else if (e.key === 'Escape') {
			// Escape để hủy
			e.preventDefault();
			cancelEdit();
		}
	};

	// Xử lý hiển thị cookie nguyên bản
	const handleShowFullCookie = (cookieText) => {
		Swal.fire({
			title: 'Chi tiết Cookie',
			html: `
				<textarea
					readonly
					style="
						width: 100%;
						height: 200px;
						font-family: monospace;
						font-size: 12px;
						line-height: 1.4;
						background-color: #f9f9f9;
						border: 1px solid #ddd;
						border-radius: 4px;
						padding: 10px;
						resize: none;
					"
				>${cookieText}</textarea>
			`,
			width: '80%',
			showConfirmButton: true,
			confirmButtonText: 'Sao chép',
			showCancelButton: true,
			cancelButtonText: 'Đóng',
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#6c757d',
			preConfirm: () => {
				const textArea = document.querySelector('textarea');
				if (textArea) {
					textArea.select();
					document.execCommand('copy');
					showNotification('info', 'Đã sao chép cookie vào clipboard!');
				}
				return false;
			}
		});
	};

	// Mở modal thêm cookie mới
	const openAddModal = () => {
		setAddModal(true);
	};

	// Đóng modal thêm cookie mới
	const closeAddModal = () => {
		setAddModal(false);
		setNewCookie({
			cookie: '',
			status: 'live',
			active: false
		});
	};

	// Cập nhật dữ liệu cookie mới
	const handleNewCookieChange = (field, value) => {
		setNewCookie(prev => ({
			...prev,
			[field]: field === 'active' ? value === 'true' : value
		}));
	};

	return (
		<div className="content">
			<Row>
				{/* Bên trái: Danh sách loại resource */}
				<Col xs="12" sm="12" md="3" lg="3">
					<Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '10px' }}>
						<CardHeader>
							<h5 style={{ fontSize: '18px', color: 'DimGray', fontWeight: 'bold' }}>
								Resource Types
							</h5>
						</CardHeader>
						<CardBody>
							<Table hover style={tableStyle}>
								<tbody>
									<tr
										style={selectedType === 'DouyinCookie' ? selectedRowStyle : tableRowStyle}
										onClick={() => handleTypeClick('DouyinCookie')}
									>
										<td style={cellStyle}>DouyinCookie</td>
									</tr>
								</tbody>
							</Table>
						</CardBody>
					</Card>
				</Col>

				{/* Bên phải: Bảng danh sách resource */}
				<Col xs="12" sm="12" md="9" lg="9">
					<Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '10px' }}>
						<CardHeader className="d-flex align-items-center">
							<h5 style={{ fontSize: '18px', color: 'DimGray', fontWeight: 'bold', marginRight: 'auto' }}>
								{selectedType} List
							</h5>
							<Button color="success" className="ml-2" onClick={openAddModal}>
								<i className="nc-icon nc-simple-add mr-1"></i> Thêm mới
							</Button>
							<Button color="info" className="ml-2" onClick={loadResources}>
								<i className="nc-icon nc-refresh-69 mr-1"></i> Tải lại
							</Button>
						</CardHeader>
						<CardBody>
							{loading ? (
								<div className="text-center p-5">
									<h4>Đang tải dữ liệu...</h4>
								</div>
							) : (
								<Table hover style={tableStyle}>
									<colgroup>
										<col style={{ width: '10%' }} />
										<col style={{ width: '15%' }} />
										<col style={{ width: '65%' }} />
										<col style={{ width: '10%' }} />
									</colgroup>
									<thead className="text-primary">
										<tr>
											<th style={cellStyle}>Kích hoạt</th>
											<th style={cellStyle}>Trạng thái</th>
											<th style={cellStyle}>Cookie</th>
											<th style={cellStyle}>Actions</th>
										</tr>
									</thead>
									<tbody>
										{cookies.map((cookie) => {
											const isSelected = selectedCookieId === cookie.id;
											const isEditing = editingRow === cookie.id;

											return (
												<tr
													key={cookie.id}
													style={isSelected ? selectedRowStyle : tableRowStyle}
													onClick={(e) => handleRowClick(cookie, e)}
													onDoubleClick={(e) => {
														e.preventDefault();
														e.stopPropagation();
														handleRowDoubleClick(cookie);
													}}
												>
													<td style={cellStyle}>
														{isEditing ? (
															<Input
																type="select"
																value={editData.active.toString()}
																onChange={(e) => handleEditChange('active', e.target.value)}
																onKeyDown={handleEditKeyDown}
																style={cellInputStyle}
																autoFocus
															>
																<option value="true">Yes</option>
																<option value="false">No</option>
															</Input>
														) : (
															<span style={{
																color: cookie.active ? 'green' : 'gray',
																fontWeight: cookie.active ? 'bold' : 'normal',
																backgroundColor: cookie.active ? '#e8f5e9' : '#f5f5f5',
																padding: '3px 8px',
																borderRadius: '4px'
															}}>
																{cookie.active ? 'Yes' : 'No'}
															</span>
														)}
													</td>
													<td style={cellStyle}>
														{isEditing ? (
															<Input
																type="select"
																value={editData.status}
																onChange={(e) => handleEditChange('status', e.target.value)}
																onKeyDown={handleEditKeyDown}
																style={cellInputStyle}
															>
																<option value="live">live</option>
																<option value="die">die</option>
															</Input>
														) : (
															<span style={{
																color: cookie.status === 'live' ? 'green' : 'red',
																fontWeight: 'bold'
															}}>
																{cookie.status}
															</span>
														)}
													</td>
													<td style={cellStyle}>
														{isEditing ? (
															<Input
																type="text"
																value={editData.cookie}
																onChange={(e) => handleEditChange('cookie', e.target.value)}
																onKeyDown={handleEditKeyDown}
																style={cellInputStyle}
															/>
														) : (
															<div style={{
																maxWidth: '300px',
																overflow: 'hidden',
																textOverflow: 'ellipsis',
																whiteSpace: 'nowrap',
																cursor: 'pointer',
																textDecoration: 'underline',
																color: '#007bff'
															}} onClick={(e) => {
																e.stopPropagation();
																handleShowFullCookie(cookie.cookie);
															}}>
																{cookie.cookie}
															</div>
														)}
													</td>
													<td style={cellStyle}>
														{isEditing ? (
															<div className="d-flex">
																<Button
																	size="sm"
																	color="primary"
																	className="mr-1"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleEditComplete();
																	}}
																>
																	<i className="nc-icon nc-check-2"></i>
																</Button>
																<Button
																	size="sm"
																	color="secondary"
																	onClick={(e) => {
																		e.stopPropagation();
																		cancelEdit();
																	}}
																>
																	<i className="nc-icon nc-simple-remove"></i>
																</Button>
															</div>
														) : (
															<div className="d-flex">
																<Button
																	size="sm"
																	color="warning"
																	className="mr-1"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleRowDoubleClick(cookie);
																	}}
																>
																	<i className="nc-icon nc-ruler-pencil"></i>
																</Button>
																<Button
																	size="sm"
																	color="danger"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleDeleteCookie(cookie);
																	}}
																>
																	<i className="nc-icon nc-simple-remove"></i>
																</Button>
															</div>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</Table>
							)}
						</CardBody>
					</Card>
				</Col>
			</Row>

			{/* Modal Thêm Cookie mới */}
			<Modal isOpen={addModal} toggle={closeAddModal}>
				<ModalHeader toggle={closeAddModal}>Thêm Cookie Mới</ModalHeader>
				<ModalBody>
					<FormGroup>
						<Label for="newCookie">Cookie</Label>
						<Input
							type="textarea"
							id="newCookie"
							rows="5"
							value={newCookie.cookie}
							onChange={(e) => handleNewCookieChange('cookie', e.target.value)}
							placeholder="Nhập cookie..."
						/>
					</FormGroup>
					<Row>
						<Col md={6}>
							<FormGroup>
								<Label for="newStatus">Trạng thái</Label>
								<Input
									type="select"
									id="newStatus"
									value={newCookie.status}
									onChange={(e) => handleNewCookieChange('status', e.target.value)}
								>
									<option value="live">live</option>
									<option value="die">die</option>
								</Input>
							</FormGroup>
						</Col>
						<Col md={6}>
							<FormGroup>
								<Label for="newActive">Kích hoạt</Label>
								<Input
									type="select"
									id="newActive"
									value={newCookie.active.toString()}
									onChange={(e) => handleNewCookieChange('active', e.target.value)}
								>
									<option value="true">Yes</option>
									<option value="false">No</option>
								</Input>
							</FormGroup>
						</Col>
					</Row>
				</ModalBody>
				<ModalFooter>
					<Button color="success" onClick={handleAddCookie}>
						<i className="nc-icon nc-simple-add mr-1"></i> Thêm
					</Button>
					<Button color="secondary" onClick={closeAddModal}>
						<i className="nc-icon nc-simple-remove mr-1"></i> Hủy
					</Button>
				</ModalFooter>
			</Modal>

			{/* Thông báo Alert ở góc phải màn hình */}
			{notification.visible && (
				<Alert
					color={notification.color}
					style={notificationStyle}
					toggle={onDismissNotification}
					fade={true}
				>
					{notification.message}
				</Alert>
			)}
		</div>
	);
}

export default Resources; 